import React from "react";

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="mb-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 min-w-0">
      <div className="min-w-0">
        <h1 className="text-2xl md:text-3xl font-bold text-gx-ink tracking-tight break-words">
          {title}
        </h1>
        {subtitle ? (
          <p className="text-sm text-gx-ink-soft mt-1">{subtitle}</p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex items-center gap-2 shrink-0 flex-wrap">{actions}</div>
      ) : null}
    </div>
  );
}

export function SectionCard({
  title,
  subtitle,
  icon,
  children,
  className = "",
  action,
  glass = false,
}) {
  return (
    <section
      className={`${
        glass ? "gx-glass" : "bg-gx-surface shadow-gx border border-gx-border"
      } rounded-gx overflow-hidden ${className}`}
    >
      {(title || action) && (
        <div className="px-5 py-4 border-b border-gx-border flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {icon ? (
              <div className="shrink-0 w-10 h-10 rounded-gx bg-gx-red/10 text-gx-red flex items-center justify-center">
                {icon}
              </div>
            ) : null}
            <div className="min-w-0">
              {title ? (
                <h2 className="text-base font-semibold text-gx-ink truncate">
                  {title}
                </h2>
              ) : null}
              {subtitle ? (
                <p className="text-xs text-gx-ink-soft truncate">{subtitle}</p>
              ) : null}
            </div>
          </div>
          {action}
        </div>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

export function StatCard({
  label,
  value,
  icon,
  accent = "#D71920",
  onClick,
  hint,
  featured = false,
}) {
  return (
    <button
      type="button"
      disabled={!onClick}
      onClick={onClick}
      className={`text-left rounded-gx transition w-full min-h-[44px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gx-red ${
        featured
          ? "gx-glass px-5 py-5 border-l-[3px] border-l-gx-red"
          : "bg-gx-surface shadow-gx border border-gx-border px-5 py-4"
      } ${
        onClick
          ? "hover:shadow-md hover:border-gx-red/30 hover:-translate-y-0.5 cursor-pointer active:scale-[0.98]"
          : "cursor-default"
      }`}
    >
      <div className="flex justify-between items-start gap-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft">
          {label}
        </p>
        {icon ? (
          <div
            className="w-11 h-11 rounded-gx flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${accent}15`, color: accent }}
          >
            {icon}
          </div>
        ) : null}
      </div>
      <p
        className={`font-bold break-words leading-tight tabular-nums ${
          featured
            ? "text-xl sm:text-3xl md:text-4xl text-gx-navy mt-4 tracking-tight"
            : "text-xl sm:text-2xl text-gx-ink mt-3"
        }`}
      >
        {value}
      </p>
      {hint ? <p className="text-xs text-gx-ink-soft mt-1">{hint}</p> : null}
    </button>
  );
}

function SkeletonBlock({ className = "" }) {
  return (
    <div
      className={`rounded-gx bg-gx-border/70 animate-pulse ${className}`}
    />
  );
}

export function DashboardSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading club overview</span>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={`kpi-${i}`} className="gx-glass rounded-gx px-5 py-5 min-h-[132px]">
            <SkeletonBlock className="h-3 w-24" />
            <SkeletonBlock className="h-10 w-28 mt-6" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="gx-glass rounded-gx p-5 lg:col-span-7 min-h-[280px]">
          <SkeletonBlock className="h-4 w-28" />
          <SkeletonBlock className="h-[220px] w-full mt-5" />
        </div>
        <div className="gx-glass rounded-gx p-5 lg:col-span-5 min-h-[280px]">
          <SkeletonBlock className="h-4 w-32" />
          <SkeletonBlock className="h-[220px] w-full mt-5" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={`stat-${i}`}
            className="bg-gx-surface border border-gx-border rounded-gx px-5 py-4 min-h-[108px]"
          >
            <SkeletonBlock className="h-3 w-20" />
            <SkeletonBlock className="h-7 w-16 mt-4" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function EmptyState({ title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-14 px-6">
      <div className="w-14 h-14 rounded-full bg-gx-canvas flex items-center justify-center mb-4 text-gx-ink-soft text-lg font-semibold">
        0
      </div>
      <h3 className="text-base font-semibold text-gx-ink">{title}</h3>
      {description ? (
        <p className="text-sm text-gx-ink-soft mt-1 max-w-sm">{description}</p>
      ) : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function FilterBar({ children, className = "" }) {
  return (
    <div
      className={`flex flex-col md:flex-row md:items-end gap-3 mb-4 ${className}`}
    >
      {children}
    </div>
  );
}

export function TableShell({ children, className = "" }) {
  return (
    <div
      className={`bg-gx-surface rounded-gx border border-gx-border overflow-hidden shadow-gx ${className}`}
    >
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

export function StatusChip({ active, label, tone }) {
  const resolved =
    tone || (active === true ? "success" : active === false ? "danger" : "neutral");
  const styles = {
    success: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
    danger: "bg-gx-red/10 text-gx-red ring-1 ring-gx-red/20",
    warning: "bg-gx-orange/10 text-gx-orange ring-1 ring-gx-orange/25",
    navy: "bg-gx-navy/10 text-gx-navy ring-1 ring-gx-navy/15",
    brand: "bg-gx-red/10 text-gx-red ring-1 ring-gx-red/20",
    neutral: "bg-gx-canvas text-gx-ink-soft ring-1 ring-gx-border",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
        styles[resolved] || styles.neutral
      }`}
    >
      {label || (active ? "Active" : "Inactive")}
    </span>
  );
}

export function initials(name) {
  if (!name || typeof name !== "string") return "GX";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  const letters = parts.map((p) => p[0]).join("");
  return letters.toUpperCase() || "GX";
}
